/**
 * Horarios fijos de salida por recorrido (itinerario oficial, Dirección de Tránsito y
 * Transporte Público de Paine). Transcritos de los PDF «Itinerario servicio» T1A…T5B.
 *
 * Los datos viven en `data/horarios.json` (raíz del repo), que comparten front y backend:
 * `horarios.json` de esta carpeta es una copia generada. Si cambia el itinerario oficial,
 * editar `data/horarios.json` y correr `python3 scripts/generate_horarios.py`.
 *
 * Estructura: recorrido → vueltas → tramos (ida / regreso) → puntos de control [nombre, 'HH:MM'].
 * - enTransito: tramo de posicionamiento del bus (en el PDF figura «EN TRÁNSITO», sin n° de
 *   expedición); no se lista como salida, solo en el detalle.
 * - variante: etiqueta corta cuando el tramo no hace el recorrido habitual.
 * - notas: avisos de la vuelta (combinaciones con metro tren, servicios directos).
 */
import horariosData from './horarios.json';

/** @typedef {{ sentido: 'ida' | 'vuelta'; puntos: [string, string][]; enTransito?: boolean; variante?: string }} HorarioTramo */
/** @typedef {{ vuelta: number; tramos: HorarioTramo[]; notas?: string[] }} HorarioVuelta */

/** @type {Record<string, HorarioVuelta[]>} */
export const HORARIOS = horariosData;

/**
 * @param {string | null | undefined} recorridoId id ya normalizado (t1a, t2, …)
 * @returns {HorarioVuelta[]}
 */
export function getHorarioVueltas(recorridoId) {
  return (recorridoId && HORARIOS[recorridoId]) || [];
}

/** Nombre más repetido de una lista (cabecera habitual de un sentido). */
function masFrecuente(nombres) {
  const cuenta = new Map();
  let mejor = null;
  for (const n of nombres) {
    const c = (cuenta.get(n) || 0) + 1;
    cuenta.set(n, c);
    if (mejor == null || c > cuenta.get(mejor)) mejor = n;
  }
  return mejor;
}

/**
 * Salidas con pasajeros de un sentido, en orden horario.
 * `detalle` solo viene cuando la expedición no parte o no termina en la cabecera habitual.
 * @param {string | null | undefined} recorridoId
 * @param {'ida' | 'vuelta'} sentido
 * @returns {{ origen: string | null; destino: string | null; salidas: { hora: string; detalle: string | null }[] }}
 */
export function getSalidas(recorridoId, sentido) {
  const tramos = getHorarioVueltas(recorridoId)
    .flatMap((v) => v.tramos)
    .filter((t) => t.sentido === sentido && !t.enTransito);
  /** Las variantes (circuitos, directos) no definen la cabecera habitual. */
  const habituales = tramos.filter((t) => !t.variante);
  const origen = masFrecuente(habituales.map((t) => t.puntos[0][0]));
  const destino = masFrecuente(habituales.map((t) => t.puntos[t.puntos.length - 1][0]));
  const salidas = tramos.map((t) => {
    const desde = t.puntos[0][0];
    const hasta = t.puntos[t.puntos.length - 1][0];
    let detalle = t.variante || null;
    if (!detalle && desde !== origen) detalle = `Desde ${desde}`;
    if (!detalle && hasta !== destino) detalle = `Hasta ${hasta}`;
    return { hora: t.puntos[0][1], detalle };
  });
  return { origen, destino, salidas };
}
